import {
  VARIABLE_PILL_ATTR,
  VARIABLE_PILL_CLASS,
} from "@/shared/utils/variable-pill";

export interface PillLocation {
  /** The pill that was clicked (may have been replaced since). */
  anchor: HTMLElement;
  /** Its `.rich-text-element` at click time — this element's own render. */
  container: HTMLElement | null;
  /** Its index among the pills of `container`. */
  anchorIndex: number;
  /** `data-variable-name` of the pill. */
  name: string;
  /** Where it was on screen at click time. */
  rect: DOMRect;
}

/**
 * The live node of a clicked pill, after a re-render may have replaced it.
 *
 * The same variable appears in many places — a duplicated slide, the
 * thumbnails panel (which even reuses the element id) — so the pill is looked
 * up in THIS element's container, by its index there. Picking "the pill with
 * this name nearest the click, horizontally" instead opened the popover on
 * the original slide when the pill of a duplicated slide was clicked: both
 * sit at the same `left`, and the tie went to the first one in the document.
 * The document-wide search only remains for a container that is gone too,
 * and measures distance on both axes.
 */
export const relocatePill = ({
  anchor,
  container,
  anchorIndex,
  name,
  rect,
}: PillLocation): HTMLElement | null => {
  if (anchor.isConnected) return anchor;
  if (container?.isConnected && anchorIndex >= 0) {
    const pill = container.querySelectorAll<HTMLElement>(
      `.${VARIABLE_PILL_CLASS}`,
    )[anchorIndex];
    if (pill?.getAttribute(VARIABLE_PILL_ATTR) === name) return pill;
  }
  const escaped = name.replace(/["\\]/g, "\\$&");
  const matches = Array.from(
    document.querySelectorAll<HTMLElement>(
      `.${VARIABLE_PILL_CLASS}[${VARIABLE_PILL_ATTR}="${escaped}"]`,
    ),
  );
  const distance = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    return Math.hypot(r.left - rect.left, r.top - rect.top);
  };
  return matches.reduce<HTMLElement | null>(
    (best, el) => (!best || distance(el) < distance(best) ? el : best),
    null,
  );
};
